import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '@store/index';
import { useAppDispatch, useAppSelector } from '@store/index';
import { validateSession, setDummyUser, selectIsAuthenticated } from '@features/auth/authSlice';
import {
  fetchCadStatus,
  fetchVerificationStatus,
} from '@features/featureFlags/featureFlagsSlice';
import './i18n/config'; // Initialize i18n

// Pages
import LoginPage from '@pages/LoginPage';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';
import ResetPasswordPage from '@pages/ResetPasswordPage';
import DashboardPage from '@pages/DashboardPage';
import DocumentsPage from '@pages/DocumentsPage';
import DocumentDetailPage from '@pages/DocumentDetailPage';
import DocumentUploadPage from '@pages/DocumentUploadPage';
import SearchPage from '@pages/SearchPage';
import UserSettingsPage from '@pages/UserSettingsPage';
import HelpPage from '@pages/HelpPage';
import AdminPage from '@pages/AdminPage';
import AuditDashboardPage from '@pages/AuditDashboardPage';
import AnomalyListPage from '@pages/AnomalyListPage';
import SettingsPage from '@pages/SettingsPage';
import NotFoundPage from '@pages/NotFoundPage';
import TestUploadPage from '@pages/TestUploadPage';
import DRBFMPage from '@pages/DRBFMPage';
import DRBFMHistoryPage from '@pages/DRBFMHistoryPage';
import VerificationPage from '@pages/VerificationPage';
import CADViewerPage from '@pages/CADViewerPage';

// Components
import PrivateRoute from '@components/auth/PrivateRoute';
import PermissionRoute from '@components/auth/PermissionRoute';
import MainLayout from '@components/layout/MainLayout';
import LoadingScreen from '@components/common/LoadingScreen';
import { Button } from '@components/ui/button';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import NotificationManager from '@components/common/NotificationManager';
import ConfirmProvider from '@components/common/ConfirmProvider';
import { AppRenderErrorBoundary } from '@components/common/AppRenderErrorBoundary';

import { useSessionManager } from './hooks/useSessionManager';

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [isInitializing, setIsInitializing] = React.useState(true);
  const [initializationError, setInitializationError] = React.useState<unknown>(null);
  const [initializationAttempt, setInitializationAttempt] = React.useState(0);
  const { t } = useTranslation();

  // Initialize session manager
  useSessionManager();

  useEffect(() => {
    const initialize = async () => {
      setIsInitializing(true);
      setInitializationError(null);
      // Skip authentication check for public routes
      const publicPaths = ['/login', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicPaths.some(path => currentPath.startsWith(path));

      if (isPublicRoute) {
        // Don't validate session on public routes to prevent redirect loop
        setIsInitializing(false);
        return;
      }

      try {
        // Validate session - HttpOnly cookie is sent automatically. The thunk
        // stores the user it validated, so there is nothing left to fetch.
        await dispatch(validateSession()).unwrap();
      } catch (error) {
        console.error('Session initialization failed:', error);
        // In development, set a dummy user if session fails
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting dummy user for development');
          await dispatch(setDummyUser());
        } else {
          setInitializationError(error ?? {});
        }
      }
      setIsInitializing(false);
    };

    initialize();
  }, [dispatch, initializationAttempt]);

  // Fetch CAD feature flag whenever auth state flips to authenticated. Covers
  // both initial session-restore and the login flow where the user enters via
  // `/login` (the initialize effect above early-returns for public routes,
  // and React Router navigation alone won't re-trigger it). The backend uses
  // the current user's tenant to resolve CAD_RESEARCH_TENANTS allowlist.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCadStatus());
      dispatch(fetchVerificationStatus());
    }
  }, [dispatch, isAuthenticated]);

  if (isInitializing) {
    return <LoadingScreen />;
  }

  if (initializationError !== null) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
        <p role="alert">{resolveApiErrorMessage(t, initializationError)}</p>
        <Button onClick={() => setInitializationAttempt(attempt => attempt + 1)}>
          {t('common.retry')}
        </Button>
      </main>
    );
  }

  return (
    <ConfirmProvider>
      <NotificationManager />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Private routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Document routes - temporarily remove permission checks for debugging */}
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/documents/upload" element={<DocumentUploadPage />} />
            <Route path="/documents/:id" element={<DocumentDetailPage />} />

            <Route path="/search" element={<SearchPage />} />
            <Route path="/drbfm" element={<DRBFMPage />} />
            <Route path="/drbfm/history" element={<DRBFMHistoryPage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/cad-viewer" element={<CADViewerPage />} />
            {/* Profile is a settings tab now; old links still resolve. */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route path="/settings" element={<UserSettingsPage />} />
            <Route path="/help" element={<HelpPage />} />

            {/* /admin/activity (read-only summary + event list + CSV
                export) accepts manager OR admin server-side. /admin/anomalies
                stays admin-only because the resolve action is admin-only —
                gating it to manager would land managers on a page where
                their primary mutation action 403s (Codex P2 on PR #585).
                Declared BEFORE the /admin/* wildcard below so RR v6
                chooses the more specific match. */}
            <Route element={<PermissionRoute requiredRoles={['admin', 'manager']} />}>
              <Route path="/admin/activity" element={<AuditDashboardPage />} />
            </Route>

            {/* Admin-only routes (config / users / data mutations / anomaly triage) */}
            <Route element={<PermissionRoute requireAdmin={true} />}>
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/anomalies" element={<AnomalyListPage />} />
              <Route path="/admin/*" element={<AdminPage />} />
              <Route path="/test-upload" element={<TestUploadPage />} />
            </Route>
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ConfirmProvider>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <AppRenderErrorBoundary>
          <AppContent />
        </AppRenderErrorBoundary>
      </Router>
    </Provider>
  );
};

export default App;
